// Вкладка «Цены»: включение актуализации цен, три источника, токен
// Travelpayouts и коридор вменяемости. Устроена как соседняя панель ИИ —
// загрузка → локальное состояние → dirty → сохранение → «сохранено», — и
// секрет здесь такой же односторонний: токен уходит на сервер, но обратно
// приходят только «есть» и последние 4 символа.
//
// Модели для веб-поиска и разбора страницы здесь нет намеренно: она живёт в
// настройках ИИ (`models.prices`), см. server/prices/README.md.
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { useI18n, useT } from '../i18n';
import type { Translate } from '../i18n';
import { errorKey } from '../auth';
import type { ApiError } from '../auth';
import {
  fetchPricesReadiness,
  fetchPricesSettings,
  savePricesSettings,
  PRICE_PROVIDERS,
} from './pricesApi';
import type { PriceProvider, PricesReadiness, PricesSettings, TravelpayoutsInfo } from './pricesApi';
import styles from './PricesPanel.module.css';

/** Границы числовых полей — из server/prices/settings.mjs (NUMERIC_BOUNDS). */
const BOUNDS = {
  maxFlights: { min: 1, max: 20, step: 1 },
  sanityMin: { min: 0.01, max: 1, step: 0.05 },
  sanityMax: { min: 1, max: 100, step: 0.5 },
} as const;

type NumericField = keyof typeof BOUNDS;

/** Поля с ошибкой: числовые плюс маркер (он сохраняется только вместе с токеном). */
type FieldErrors = Partial<Record<NumericField | 'marker', string>>;

/**
 * Сообщение об отказе сервера. Коды у /api/admin/prices те же, что у остальной
 * админки, а `message` — готовая фраза от сервера («sanityMin: нижняя граница
 * коридора выше верхней»), и терять её нельзя: она объясняет отказ точнее
 * любого общего текста.
 */
function detail(t: Translate, error: ApiError): string {
  if (error.message) return error.message;
  if (error.error === 'network') return t('auth.err.network');
  const key = errorKey(error);
  const text = t(key);
  return text === key ? error.error : text;
}

export function PricesPanel(): ReactElement {
  const t = useT();
  const { lang } = useI18n();

  const [settings, setSettings] = useState<PricesSettings | null>(null);
  const [token, setToken] = useState<TravelpayoutsInfo | null>(null);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const [updatedBy, setUpdatedBy] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<ApiError | null>(null);

  // Готовность источников — с /api/prices/config: там сервер отвечает не тем,
  // что стоит в галочках, а тем, что реально работает (есть токен, есть модель
  // и ключ). Галочка без токена — выключенный источник, и панель обязана
  // говорить это вслух, а не пересказывать саму себя.
  const [readiness, setReadiness] = useState<PricesReadiness | null>(null);

  const [tokenInput, setTokenInput] = useState('');
  const [markerInput, setMarkerInput] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<ApiError | null>(null);
  const [note, setNote] = useState<'saved' | 'cleared' | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const applyView = useCallback(
    (view: {
      settings: PricesSettings;
      travelpayouts: TravelpayoutsInfo;
      updatedAt: number | null;
      updatedBy: string;
    }) => {
      setSettings(view.settings);
      setToken(view.travelpayouts);
      setMarkerInput(view.travelpayouts.marker);
      setUpdatedAt(view.updatedAt);
      setUpdatedBy(view.updatedBy);
    },
    [],
  );

  const loadReadiness = useCallback(async () => {
    const res = await fetchPricesReadiness();
    if (res.ok) setReadiness(res.value);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetchPricesSettings();
    setLoading(false);
    if (res.ok) {
      applyView(res.value);
      setLoadError(null);
      setDirty(false);
    } else {
      setLoadError(res.error);
    }
  }, [applyView]);

  useEffect(() => {
    void load();
    void loadReadiness();
  }, [load, loadReadiness]);

  function update(patch: Partial<PricesSettings>): void {
    setSettings((s) => (s ? { ...s, ...patch } : s));
    setDirty(true);
    setNote(null);
  }

  function toggleProvider(name: PriceProvider, value: boolean): void {
    setSettings((s) => (s ? { ...s, providers: { ...s.providers, [name]: value } } : s));
    setDirty(true);
    setNote(null);
  }

  // Маркер отличается от сохранённого — значит его правили. Один маркер, без
  // токена, сервер сохранит только поверх токена, лежащего в БД: маркер и токен
  // живут одной строкой, а метка для ссылки, которую неоткуда взять, сервису не
  // нужна. Токен из окружения этому условию не отвечает — его в этой строке нет.
  const markerDirty = token !== null && markerInput.trim() !== token.marker;
  const tokenTyped = tokenInput.trim().length > 0;
  const tokenInDb = token?.source === 'db';

  const errors = useMemo<FieldErrors>(() => {
    const found: FieldErrors = {};
    if (!settings) return found;
    (Object.keys(BOUNDS) as NumericField[]).forEach((field) => {
      const { min, max } = BOUNDS[field];
      const value = settings[field];
      if (!Number.isFinite(value) || value < min || value > max) {
        found[field] = `${t('admin.prices.err.range')} (${min}–${max})`;
      }
    });
    // Перевёрнутый коридор пропустил бы любую цену — сервер такой набор
    // отвергает, и до отказа доводить незачем.
    if (!found.sanityMin && !found.sanityMax && settings.sanityMin >= settings.sanityMax) {
      found.sanityMin = t('admin.prices.err.range');
    }
    if (markerDirty && !tokenTyped && !tokenInDb) found.marker = t('admin.prices.marker.needsToken');
    return found;
  }, [settings, markerDirty, tokenTyped, tokenInDb, t]);

  const hasErrors = Object.keys(errors).length > 0;
  const canSave = (dirty || tokenTyped || markerDirty) && !hasErrors && !saving && settings !== null;

  async function handleSave(): Promise<void> {
    if (!settings || hasErrors) return;
    setSaving(true);
    setSaveError(null);
    // Пару «токен + маркер» отправляем, только когда её трогали: пустое поле
    // токена сервер понимает как «оставить сохранённый», а лишний запрос без
    // правок незачем. Стирает токен исключительно кнопка ниже, явным null.
    const res = await savePricesSettings({
      settings,
      ...(tokenTyped || markerDirty
        ? { travelpayouts: { token: tokenInput.trim(), marker: markerInput.trim() } }
        : {}),
    });
    setSaving(false);
    if (res.ok) {
      applyView(res.value);
      setTokenInput(''); // секрету в состоянии формы жить незачем
      setDirty(false);
      setNote('saved');
      void loadReadiness();
    } else {
      setSaveError(res.error);
    }
  }

  async function handleClearToken(): Promise<void> {
    setSaving(true);
    setSaveError(null);
    const res = await savePricesSettings({ travelpayouts: null });
    setSaving(false);
    if (res.ok) {
      applyView(res.value);
      setTokenInput('');
      setNote('cleared');
      void loadReadiness();
    } else {
      setSaveError(res.error);
    }
  }

  const formatDate = useCallback(
    (ms: number | null) =>
      ms ? new Intl.DateTimeFormat(lang, { dateStyle: 'short', timeStyle: 'short' }).format(ms) : '—',
    [lang],
  );

  if (loading) return <p className={styles.muted}>{t('auth.busy')}</p>;

  if (loadError) {
    return (
      <p className={styles.error} role="alert">
        {t('admin.prices.err.load')}: {detail(t, loadError)}
      </p>
    );
  }

  // Успех без тела неотличим от «ещё грузится» — и подпись поэтому та же.
  if (!settings || !token) return <p className={styles.muted}>{t('auth.busy')}</p>;

  return (
    <div className={styles.panel}>
      <section className={styles.block}>
        <h3 className={styles.subtitle}>{t('admin.prices.title')}</h3>
        <label className={styles.switchRow}>
          <input
            type="checkbox"
            checked={settings.enabled}
            onChange={(e) => update({ enabled: e.target.checked })}
          />
          <span>{t('admin.prices.enabled')}</span>
        </label>
        <p className={styles.hint}>{t('admin.prices.enabled.hint')}</p>
      </section>

      <section className={styles.block}>
        <h3 className={styles.subtitle}>{t('admin.prices.providers')}</h3>
        <ul className={styles.providers}>
          {PRICE_PROVIDERS.map((name) => {
            const on = settings.providers[name];
            const ready = readiness?.providers?.[name] === true;
            return (
              <li key={name} className={styles.provider}>
                <div className={styles.providerHead}>
                  <label className={styles.switchRow}>
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={(e) => toggleProvider(name, e.target.checked)}
                    />
                    <span>{t(`admin.prices.provider.${name}`)}</span>
                  </label>
                  {/* Значок готовности показываем только у включённого источника:
                      у выключенного «не работает» — не новость, а шум. Значок
                      описывает то, что на сервере сейчас, поэтому у только что
                      поставленной и ещё не сохранённой галочки он честно
                      говорит «не работает» — до сохранения так и есть. */}
                  {on && (
                    <span className={ready ? styles.ready : styles.notReady}>
                      {t(ready ? 'admin.prices.provider.ready' : 'admin.prices.provider.notReady')}
                    </span>
                  )}
                </div>
                <p className={styles.hint}>{t(`admin.prices.provider.${name}.hint`)}</p>
              </li>
            );
          })}
        </ul>
      </section>

      <section className={styles.block}>
        <h3 className={styles.subtitle}>{t('admin.prices.token')}</h3>
        {token.present && (
          <p className={styles.tokenStatus}>
            {t('admin.prices.token.present', { tail: token.tail })}
          </p>
        )}
        <div className={styles.tokenRow}>
          <input
            id="prices-token"
            className="input"
            type="password"
            autoComplete="off"
            aria-label={t('admin.prices.token')}
            aria-describedby="prices-token-hint"
            value={tokenInput}
            onChange={(e) => {
              setTokenInput(e.target.value);
              setNote(null);
            }}
          />
          {/* Токен из окружения админке не принадлежит — стереть его отсюда
              нельзя, поэтому кнопка выключена, как и у ключа ИИ. */}
          <button
            type="button"
            className="btn btn-sm btn-danger"
            disabled={saving || token.source !== 'db'}
            onClick={() => void handleClearToken()}
          >
            {t('admin.prices.token.clear')}
          </button>
        </div>
        <p id="prices-token-hint" className={styles.hint}>
          {t('admin.prices.token.hint')}
        </p>

        <div className={styles.field}>
          <label htmlFor="prices-marker" className="field-label">
            {t('admin.prices.marker')}
          </label>
          <input
            id="prices-marker"
            className="input"
            type="text"
            autoComplete="off"
            value={markerInput}
            aria-invalid={errors.marker ? true : undefined}
            aria-describedby={errors.marker ? 'prices-marker-error' : 'prices-marker-hint'}
            onChange={(e) => {
              setMarkerInput(e.target.value);
              setNote(null);
            }}
          />
          {errors.marker ? (
            <p id="prices-marker-error" className="field-error">
              {errors.marker}
            </p>
          ) : (
            <p id="prices-marker-hint" className={styles.hint}>
              {t('admin.prices.marker.hint')}
            </p>
          )}
        </div>
      </section>

      {/* Тонкая настройка свёрнута, как и у панели ИИ: значения по умолчанию
          рабочие, и трогают их редко. */}
      <section className={styles.advanced}>
        <button
          type="button"
          className="btn btn-sm"
          aria-expanded={advancedOpen}
          aria-controls="prices-advanced"
          onClick={() => setAdvancedOpen((v) => !v)}
        >
          {t('admin.prices.advanced')}
        </button>
        {advancedOpen && (
          <div id="prices-advanced" className={styles.advancedBody}>
            <div className={styles.field}>
              <label htmlFor="prices-maxFlights" className="field-label">
                {t('admin.prices.maxFlights')}
              </label>
              <input
                id="prices-maxFlights"
                className="input"
                type="number"
                min={BOUNDS.maxFlights.min}
                max={BOUNDS.maxFlights.max}
                step={BOUNDS.maxFlights.step}
                value={Number.isFinite(settings.maxFlights) ? settings.maxFlights : ''}
                aria-invalid={errors.maxFlights ? true : undefined}
                aria-describedby={
                  errors.maxFlights ? 'prices-maxFlights-error' : 'prices-maxFlights-hint'
                }
                onChange={(e) => update({ maxFlights: e.target.valueAsNumber })}
              />
              {errors.maxFlights ? (
                <p id="prices-maxFlights-error" className="field-error">
                  {errors.maxFlights}
                </p>
              ) : (
                <p id="prices-maxFlights-hint" className={styles.hint}>
                  {t('admin.prices.maxFlights.hint')}
                </p>
              )}
            </div>

            <fieldset className={styles.sanity}>
              <legend className={styles.legend}>{t('admin.prices.sanity')}</legend>
              <p id="prices-sanity-hint" className={styles.hint}>
                {t('admin.prices.sanity.hint')}
              </p>
              <div className={styles.sanityGrid}>
                <div className={styles.field}>
                  <label htmlFor="prices-sanityMin" className="field-label">
                    {t('admin.prices.sanityMin')}
                  </label>
                  <input
                    id="prices-sanityMin"
                    className="input"
                    type="number"
                    min={BOUNDS.sanityMin.min}
                    max={BOUNDS.sanityMin.max}
                    step={BOUNDS.sanityMin.step}
                    value={Number.isFinite(settings.sanityMin) ? settings.sanityMin : ''}
                    aria-invalid={errors.sanityMin ? true : undefined}
                    aria-describedby={
                      errors.sanityMin ? 'prices-sanityMin-error' : 'prices-sanity-hint'
                    }
                    onChange={(e) => update({ sanityMin: e.target.valueAsNumber })}
                  />
                  {errors.sanityMin && (
                    <p id="prices-sanityMin-error" className="field-error">
                      {errors.sanityMin}
                    </p>
                  )}
                </div>
                <div className={styles.field}>
                  <label htmlFor="prices-sanityMax" className="field-label">
                    {t('admin.prices.sanityMax')}
                  </label>
                  <input
                    id="prices-sanityMax"
                    className="input"
                    type="number"
                    min={BOUNDS.sanityMax.min}
                    max={BOUNDS.sanityMax.max}
                    step={BOUNDS.sanityMax.step}
                    value={Number.isFinite(settings.sanityMax) ? settings.sanityMax : ''}
                    aria-invalid={errors.sanityMax ? true : undefined}
                    aria-describedby={
                      errors.sanityMax ? 'prices-sanityMax-error' : 'prices-sanity-hint'
                    }
                    onChange={(e) => update({ sanityMax: e.target.valueAsNumber })}
                  />
                  {errors.sanityMax && (
                    <p id="prices-sanityMax-error" className="field-error">
                      {errors.sanityMax}
                    </p>
                  )}
                </div>
              </div>
            </fieldset>
          </div>
        )}
      </section>

      <div className={styles.saveBar}>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!canSave}
          onClick={() => void handleSave()}
        >
          {t('admin.prices.save')}
        </button>
        {/* Область живёт в разметке всегда: скринридер объявляет изменение
            текста, а не появление самого элемента. */}
        <span className={styles.ok} role="status">
          {note === 'saved'
            ? t('admin.prices.saved')
            : note === 'cleared'
              ? t('admin.prices.token.cleared')
              : ''}
        </span>
        {saveError && (
          <span className={styles.error} role="alert">
            {t('admin.prices.err.save')}: {detail(t, saveError)}
          </span>
        )}
        <span className={styles.updated}>
          {t('admin.prices.updated', { when: formatDate(updatedAt), who: updatedBy || '—' })}
        </span>
      </div>
    </div>
  );
}
